import { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';

interface ServerVersion {
  version: string;
  commit: string;
  buildTime: string;
}

export function useVersionCheck() {
  const [serverVersion, setServerVersion] = useState<ServerVersion | null>(null);
  const [isOutdated, setIsOutdated] = useState<boolean>(false);
  const [checking, setChecking] = useState<boolean>(false);

  const clientVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.2.0';
  const clientCommit = typeof __COMMIT_HASH__ !== 'undefined' ? __COMMIT_HASH__ : 'dev';
  const clientBuildTime = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '';

  const checkVersion = async () => {
    try {
      setChecking(true);
      const res = await fetch(`/api/version?_cb=${Date.now()}`, {
        cache: 'no-store',
        headers: { pragma: 'no-cache', 'cache-control': 'no-cache' },
      });
      if (res.ok) {
        const data = (await res.json()) as ServerVersion;
        setServerVersion(data);
        const outdated =
          (data.commit !== clientCommit && clientCommit !== 'dev' && data.commit !== 'latest') ||
          data.version !== clientVersion;
        setIsOutdated(outdated);
      }
    } catch {
      // Offline or network error
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    void checkVersion();
    const interval = setInterval(() => {
      void checkVersion();
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  const forceRefresh = () => {
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }
    const cleanUrl = window.location.pathname;
    window.location.href = `${cleanUrl}?_reload=${Date.now()}`;
  };

  return {
    clientVersion,
    clientCommit,
    clientBuildTime,
    serverVersion,
    isOutdated,
    checking,
    checkVersion,
    forceRefresh,
  };
}

/** Sticky alert shown when the browser displays a cached version older than the server */
export function VersionCacheBanner() {
  const { clientVersion, clientCommit, serverVersion, isOutdated, forceRefresh } = useVersionCheck();

  if (!isOutdated || !serverVersion) return null;

  return (
    <div
      role="alert"
      className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-amber-600 via-amber-700 to-amber-800 px-4 py-2.5 text-xs text-white shadow-md animate-fade-in"
    >
      <div className="flex items-center gap-2.5">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75"></span>
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white"></span>
        </span>
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-200" />
        <span>
          <strong>Version en cache détectée !</strong> Votre navigateur affiche la version{' '}
          <code className="rounded bg-black/20 px-1.5 py-0.5 font-mono font-bold">
            v{clientVersion} ({clientCommit})
          </code>
          , mais une nouvelle version est en ligne :{' '}
          <code className="rounded bg-white/20 px-1.5 py-0.5 font-mono font-bold text-amber-100">
            v{serverVersion.version} ({serverVersion.commit})
          </code>
          .
        </span>
      </div>
      <button
        type="button"
        onClick={forceRefresh}
        className="flex items-center gap-1.5 rounded-lg bg-white px-3 py-1 font-semibold text-amber-900 shadow-xs hover:bg-amber-50 active:scale-95 transition-all text-xs cursor-pointer"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Actualiser (recharger la version actuelle)
      </button>
    </div>
  );
}

/** Interactive version badge for sidebar / header */
export function VersionBadge({ compact = false }: { compact?: boolean }) {
  const { clientVersion, clientCommit, clientBuildTime, isOutdated, forceRefresh, serverVersion } =
    useVersionCheck();
  const [showDetails, setShowDetails] = useState(false);

  const formattedDate = clientBuildTime
    ? new Date(clientBuildTime).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  return (
    <div className="relative inline-flex items-center">
      <button
        type="button"
        onClick={() => setShowDetails((prev) => !prev)}
        className={`group inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono font-bold transition-all select-none cursor-pointer ${
          isOutdated
            ? 'border-amber-500/40 bg-amber-50 text-amber-800 hover:bg-amber-100'
            : 'border-herb-600/20 bg-herb-50 text-herb-700 hover:bg-herb-100'
        }`}
        title={`Version v${clientVersion} (${clientCommit}) · Cliquez pour inspecter`}
      >
        <span
          className={`h-1.5 w-1.5 rounded-full ${isOutdated ? 'bg-amber-500 animate-pulse' : 'bg-herb-600'}`}
        />
        <span>v{clientVersion}</span>
        {!compact && <span className="text-ink-faint">· {clientCommit}</span>}
      </button>

      {showDetails && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowDetails(false)}
            aria-hidden="true"
          />
          <div className="absolute left-0 top-full mt-2 z-50 w-64 rounded-xl border border-border bg-card p-3 shadow-xl text-xs space-y-2.5 text-ink animate-rise">
            <div className="flex items-center justify-between border-b border-border pb-1.5">
              <span className="font-bold text-ink">Informations de version</span>
              {isOutdated ? (
                <span className="rounded bg-amber-100 text-amber-800 px-1.5 py-0.2 text-[9px] font-bold uppercase">
                  Cache détecté
                </span>
              ) : (
                <span className="rounded bg-herb-100 text-herb-800 px-1.5 py-0.2 text-[9px] font-bold uppercase flex items-center gap-1">
                  <CheckCircle2 className="w-2.5 h-2.5" /> À jour
                </span>
              )}
            </div>

            <div className="space-y-1 font-mono text-[11px] text-ink-muted">
              <div className="flex justify-between">
                <span>Version locale :</span>
                <span className="font-bold text-ink">v{clientVersion}</span>
              </div>
              <div className="flex justify-between">
                <span>Commit :</span>
                <span className="font-bold text-ink">{clientCommit}</span>
              </div>
              {formattedDate && (
                <div className="flex justify-between">
                  <span>Build :</span>
                  <span className="text-ink-soft">{formattedDate}</span>
                </div>
              )}
              {serverVersion && (
                <div className="flex justify-between border-t border-border/60 pt-1 text-ink-soft">
                  <span>Serveur :</span>
                  <span className="font-bold text-ink">
                    v{serverVersion.version} ({serverVersion.commit})
                  </span>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={forceRefresh}
              className="w-full mt-1 flex items-center justify-center gap-1.5 rounded-lg bg-herb-600 hover:bg-herb-700 active:scale-98 text-white px-3 py-1.5 text-xs font-semibold shadow-xs transition-all cursor-pointer"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Forcer l'actualisation
            </button>
          </div>
        </>
      )}
    </div>
  );
}
